import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TOUR_COPY, TOUR_GEOMETRY } from "@/constants/tour";
import { useFocusTrap } from "@/hooks/useFocusTrap";
import type { CardPosition, Size, TourStep } from "@/interfaces/tour";
import type { SpotlightRect } from "@/interfaces/tutorial";
import { measureElements, scrollFirstIntoView } from "@/utils/dom-helper";
import {
  centerCard,
  padRect,
  paintsMeasuredPlacement,
  placeCard,
  unionRects,
} from "@/utils/tour-helper";
import TourCard from "./TourCard";

const HOLE_CLASS =
  "fixed pointer-events-auto rounded-[12px] shadow-[0_0_0_9999px_rgba(30,26,22,0.42)]";
const HOLE_ANIMATED_CLASS =
  "transition-[top,left,width,height] duration-500 ease-[cubic-bezier(.22,1,.36,1)]";

/**
 * Hole collapsed onto the viewport centre: the dim stays, nothing is lit.
 *
 * Percentages, not measured pixels, so this is a constant the very first frame of a transition can
 * render — there is no window to read and no state for a later effect to correct. `top`/`left`
 * interpolate between a length and a percentage, so the lit hole still animates shut.
 */
const COLLAPSED_HOLE_STYLE = { top: "50%", left: "50%", width: 0, height: 0 } as const;

function readViewport(): Size {
  return { width: window.innerWidth, height: window.innerHeight };
}

/** One measurement pass: where the hole and the card go, and which step that was measured for. */
interface Placement {
  /** Index within `steps` at measure time. A placement for another step is never painted. */
  index: number;
  /** null when there is nothing to light (no targets, or none resolved before the timeout). */
  hole: SpotlightRect | null;
  card: CardPosition;
}

interface SpotlightOverlayProps {
  open: boolean;
  steps: readonly TourStep[];
  /** 0-based index into `steps`. */
  index: number;
  /**
   * True while the owner navigates or waits for targets. Rendered straight from this prop: the
   * card is centred by CSS and nothing is lit, so a transition never paints a measured value.
   */
  isSettling: boolean;
  onNext: () => void;
  onBack: () => void;
  onEnd: () => void;
}

/**
 * The guided-tour engine shared by the onboarding tour (`SpotlightTour`) and the campaign
 * builder's design tour (`TutorialModal`): one dim with a lit hole over the current step's
 * targets, and the card beside it. It owns geometry, keyboard and focus only — which steps exist,
 * and what each one needs (a route, a canvas tab), belongs to the caller.
 *
 * Portalled into `document.body`: `position: fixed` resolves against the nearest transformed
 * ancestor, and the dashboard's `.gfg-rise` entrance holds a transform for a full second.
 */
export default function SpotlightOverlay({
  open,
  steps,
  index,
  isSettling,
  onNext,
  onBack,
  onEnd,
}: SpotlightOverlayProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const hasPlacedRef = useRef(false);
  const instantTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [placement, setPlacement] = useState<Placement | null>(null);
  const [entered, setEntered] = useState(false);
  /** The first spotlight appears where it belongs instead of sliding in from the centre. */
  const [instant, setInstant] = useState(true);

  const step = steps[index];

  useFocusTrap(open, cardRef);

  useEffect(() => {
    if (!open) return;
    const frame = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(frame);
  }, [open]);

  // Clean slate on close, so the next run starts instant and invisible again.
  useEffect(() => {
    if (open) return;
    if (instantTimerRef.current !== null) {
      clearTimeout(instantTimerRef.current);
      instantTimerRef.current = null;
    }
    hasPlacedRef.current = false;
    setPlacement(null);
    setEntered(false);
    setInstant(true);
  }, [open]);

  useEffect(() => {
    return () => {
      if (instantTimerRef.current !== null) clearTimeout(instantTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!open || !step) return;

    // Hidden until measured, so the card never appears before its coordinates are known — and
    // dropped at the START of every run, the settling pass included, hence BEFORE the early return
    // below. Re-entering the same index (the design tour re-runs this whenever its canvas tab or a
    // callback identity changes) otherwise leaves a placement whose `index` still matches, which
    // `paintsMeasuredPlacement` has no way to reject. Here is early enough: React flushes this
    // effect before the commit that ends the settling pass renders.
    setPlacement(null);

    // Nothing to measure while settling: that state is rendered from `isSettling` alone, so the
    // card is already centred with the entered step's copy and nothing is lit.
    if (isSettling) return;

    const place = () => {
      const viewport = readViewport();
      const card = cardRef.current;
      const cardSize: Size = {
        width: card?.offsetWidth ?? TOUR_GEOMETRY.cardWidth,
        height: card?.offsetHeight ?? 0,
      };
      const target = unionRects(measureElements(step.targets));

      // No targets (closing step) or none resolved (timed out): centre the card, light nothing.
      if (target === null) {
        setPlacement({ index, hole: null, card: centerCard(cardSize, viewport) });
      } else {
        // The hole is padded; the card is placed against the raw target rect.
        setPlacement({
          index,
          hole: padRect(target, TOUR_GEOMETRY.pad),
          card: placeCard(
            target,
            step.place,
            cardSize,
            viewport,
            TOUR_GEOMETRY.gap,
            TOUR_GEOMETRY.margin,
          ),
        });
      }

      if (!hasPlacedRef.current) {
        hasPlacedRef.current = true;
        instantTimerRef.current = setTimeout(() => {
          instantTimerRef.current = null;
          setInstant(false);
        }, TOUR_GEOMETRY.instantFirstStepMs);
      }
    };

    let frame = 0;

    // A target below the fold would be lit off-screen, so bring it into view first and read the
    // rects one frame later, once the scroll has landed.
    const measure = () => {
      if (!scrollFirstIntoView(step.targets)) {
        place();
        return;
      }
      frame = requestAnimationFrame(place);
    };

    frame = requestAnimationFrame(measure);

    // Capture: the sidebar nav and the account panels are nested scroll containers and their
    // scroll events do not bubble. This re-places WITHOUT re-scrolling — routing it through
    // `measure` would yank back any deliberate scroll by the user.
    const onViewportChange = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(place);
    };
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, { passive: true, capture: true });

    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
    };
  }, [open, isSettling, step, index]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      // Enter inside the card belongs to that control — focus is trapped there, so swallowing it
      // would advance the tour instead of pressing Close / Skip / Back.
      const insideCard =
        event.key === "Enter" && cardRef.current?.contains(event.target as Node) === true;
      if (insideCard) return;

      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        onEnd();
      } else if (event.key === "ArrowRight" || event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();
        onNext();
      } else if (event.key === "ArrowLeft") {
        event.preventDefault();
        event.stopPropagation();
        onBack();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => document.removeEventListener("keydown", onKeyDown, true);
  }, [open, onEnd, onNext, onBack]);

  if (!open || !step) return null;

  // Derived here, in render, never read back from state a passive effect writes after paint.
  const placed =
    placement !== null && paintsMeasuredPlacement(placement.index, index, isSettling)
      ? placement
      : null;
  const holeRect = placed?.hole ?? null;
  const cardPosition: CardPosition | "center" | null = isSettling
    ? "center"
    : (placed?.card ?? null);

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={TOUR_COPY.dialogAria(index + 1, steps.length, step.title)}
      tabIndex={-1}
      className="fixed inset-0 z-[9000] transition-opacity duration-500 ease-[ease]"
      style={{ opacity: entered ? 1 : 0 }}
    >
      <div
        aria-hidden="true"
        className={instant ? HOLE_CLASS : `${HOLE_CLASS} ${HOLE_ANIMATED_CLASS}`}
        style={
          holeRect === null
            ? COLLAPSED_HOLE_STYLE
            : {
                top: holeRect.top,
                left: holeRect.left,
                width: holeRect.width,
                height: holeRect.height,
              }
        }
      />
      <TourCard
        step={step}
        index={index}
        total={steps.length}
        position={cardPosition}
        containerRef={cardRef}
        onBack={onBack}
        onNext={onNext}
        onEnd={onEnd}
      />
    </div>,
    document.body,
  );
}
